import {
	BasicButton,
	SecondCustomInput,
	SelectBase,
	Typography,
} from "@/components";
import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import useProfile from "@/hooks/useProfile";
import { postRegister } from "@/redux/reducers/auth";
import { ProfessionalCredentialFieldsSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import OutlinedFlagRoundedIcon from "@mui/icons-material/OutlinedFlagRounded";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PortraitRoundedIcon from "@mui/icons-material/PortraitRounded";
import Box from "@mui/material/Box";
import { createFileRoute } from "@tanstack/react-router";
import CountryList from "country-list-with-dial-code-and-flag";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

export const Route = createFileRoute(
	"/dashboard/accounts/settings/profile/credentials",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const countries = CountryList.getAll();
	const dispatch = useDispatch();
	const handleOnBack = useBackward();

	const { profile: userData } = useProfile();

	const {
		control,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<ProfessionalCredentialFieldsSchema>({
		mode: "all",
		resolver: zodResolver(ProfessionalCredentialFieldsSchema),
		defaultValues: async () => ({
			country: userData?.country ?? "",
			profession: userData?.profession ?? "",
			full_name: userData?.fullname ?? "",
			workplace: userData?.hospital?.name ?? "",
			registration: userData?.registration_number ?? "",
			referral_code: userData?.referral_code ?? "",
		}),
	});

	const onSubmit: SubmitHandler<ProfessionalCredentialFieldsSchema> = (
		data,
		e,
	) => {
		e?.stopPropagation();
		e?.preventDefault();
		const nameParts = data.full_name.split(" ");
		const first_name = nameParts[0];
		const last_name = nameParts.slice(1).join(" ");
		return dispatch(
			postRegister({
				first_name: first_name,
				last_name: last_name,
				country: data.country,
				profession: data.profession,
				hospital_name: data.workplace,
				registration_number: data.registration,
				referral_code: data.referral_code,
				callback: () => {
					handleOnBack();
					toast.success("Professional credentials updated");
				},
			}),
		);
	};

	return (
		<div className="wrapper !p-0 min-h-full mx-auto">
			<BackAppBar />
			<form onSubmit={handleSubmit(onSubmit)} className="container">
				<Typography fontWeight={600} fontSize={28}>
					Professional credentials
				</Typography>
				<Box className="py-4 mx-auto">
					<Box className="mb-6">
						<Controller
							control={control}
							defaultValue=""
							name="country"
							render={({ field, fieldState }) => (
								<SelectBase
									placeholder="Country"
									name="country"
									label="Country"
									startIcon={<OutlinedFlagRoundedIcon />}
									options={countries}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									errorMessage={fieldState.error?.message}
								/>
							)}
						/>
					</Box>
					<Box className="mb-6">
						<Controller
							control={control}
							defaultValue=""
							name="profession"
							render={({ field, fieldState }) => (
								<SelectBase
									placeholder="Profession"
									name="profession"
									label="Profession"
									startIcon={<PortraitRoundedIcon />}
									options={[
										{ id: "1", name: "Doctor" },
										{ id: "2", name: "Nurse" },
										{ id: "3", name: "Midwife" },
										{ id: "4", name: "Dentist" },
										{ id: "5", name: "Occupational Therapist" },
										{ id: "6", name: "Physiotherapist" },
										{ id: "7", name: "Pharmacist" },
										{ id: "8", name: "Psychologist" },
										{ id: "9", name: "Social Worker" },
										{ id: "10", name: "Admin Staff" },
										{ id: "11", name: "Medical Student" },
										{ id: "12", name: "Other Health Care Professionals" },
									]}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									errorMessage={fieldState.error?.message}
								/>
							)}
						/>
					</Box>
					<Box className="mb-6">
						<Controller
							control={control}
							defaultValue=""
							name="full_name"
							render={({ field, fieldState }) => (
								<SecondCustomInput
									type="text"
									label="Full Name"
									name="name"
									startIcon={<PersonOutlineOutlinedIcon />}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									errorMessage={fieldState.error?.message}
								/>
							)}
						/>
					</Box>
					<Box className="mb-6">
						<Controller
							control={control}
							defaultValue=""
							name="workplace"
							render={({ field, fieldState }) => (
								<SecondCustomInput
									type="text"
									label="Workplace"
									name="workplace"
									startIcon={<LocalHospitalOutlinedIcon />}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									errorMessage={fieldState.error?.message}
								/>
							)}
						/>
					</Box>
					<Box className="mb-6">
						<Controller
							control={control}
							defaultValue=""
							name="registration"
							render={({ field, fieldState }) => (
								<SecondCustomInput
									type="text"
									label="Professional Registration/Passport/ID No."
									name="registration"
									startIcon={<BadgeOutlinedIcon />}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									errorMessage={fieldState.error?.message}
								/>
							)}
						/>
					</Box>
					<Box className="mb-6">
						<Controller
							control={control}
							defaultValue=""
							name="referral_code"
							render={({ field, fieldState }) => (
								<SecondCustomInput
									type="text"
									label="Referral Code (Optional)"
									name="refferal-code"
									startIcon={<BadgeOutlinedIcon />}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									errorMessage={fieldState.error?.message}
								/>
							)}
						/>
					</Box>
				</Box>
				{/* button submit  */}
				<Box className="fixed bg-white shadow bottom-0 inset-x-0 z-50 py-3">
					<Box className="wrapper mx-auto">
						<BasicButton
							type="submit"
							variant="contained"
							color="secondary"
							className="w-full !h-12 !text-sm font-semibold !shadow-none"
							disabled={!isDirty || !isValid || isSubmitting}
							loading={isSubmitting}
						>
							Next
						</BasicButton>
					</Box>
				</Box>
			</form>
		</div>
	);
}
